import { CameraIcon, SignalIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";

import { RfidScanner } from "../utils/rfidScanner";

import CameraEpcModal from "./CameraEpcModal";
import { Button } from "./ui/button";

/**
 * EPC capture bar — three input paths, mirroring the gen5-pwa flow:
 *   1. R6 hand scanner over Web Bluetooth (UHF antenna read / barcode trigger)
 *   2. the phone camera (QR/barcode, via CameraEpcModal)
 *   3. manual type / paste
 *
 * Every captured EPC (uppercased) is forwarded to `onEpc`. When `onBarcode`
 * is supplied, R6 barcode-trigger reads are routed there INSTEAD of `onEpc`
 * (RFID tag reads still go to `onEpc`). The barcode text is forwarded
 * verbatim — no uppercase, no format check — since work-order codes can
 * be case- and character-sensitive.
 */
interface EpcScannerProps {
  onEpc: (epc: string) => void;
  onBarcode?: (text: string) => void;
}

export const EpcScanner: React.FC<EpcScannerProps> = ({ onEpc, onBarcode }) => {
  const bleAvailable = RfidScanner.isWebBluetoothAvailable;
  const scannerRef = useRef<RfidScanner | null>(null);

  const [connected, setConnected] = useState(false);
  const [deviceName, setDeviceName] = useState<string | null>(null);
  const [battery, setBattery] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [manual, setManual] = useState("");

  // Keep latest callbacks without re-binding the scanner listeners each render.
  const onEpcRef = useRef(onEpc);
  const onBarcodeRef = useRef(onBarcode);
  useEffect(() => {
    onEpcRef.current = onEpc;
  }, [onEpc]);
  useEffect(() => {
    onBarcodeRef.current = onBarcode;
  }, [onBarcode]);

  // Disconnect the R6 on unmount so the BLE link doesn't leak across pages.
  useEffect(() => {
    return () => {
      scannerRef.current?.disconnectBle();
      scannerRef.current = null;
    };
  }, []);

  const connect = async (): Promise<void> => {
    setBusy(true);
    setStatus("Select your hand scanner in the browser prompt…");
    try {
      const scanner = new RfidScanner();
      scanner.onTagScanned = (epc) => onEpcRef.current(epc.toUpperCase());
      scanner.onBarcodeScanned = (text) => {
        const cb = onBarcodeRef.current;
        if (cb) cb(text);
        else onEpcRef.current(text.toUpperCase());
      };
      scanner.onBatteryLevel = (lvl) => setBattery(lvl);
      scanner.onBleStatusChange = (isConnected, info) => {
        setConnected(isConnected);
        if (!isConnected) setStatus(`${info.name} disconnected`);
      };
      const info = await scanner.discoverAndConnect();
      scannerRef.current = scanner;
      setDeviceName(info.name);
      setConnected(true);
      setStatus("Initializing UHF module…");
      await scanner.initUhfModule(24, (m) => setStatus(m));
      void scanner.getBattery();
      setStatus("Connected — pull the trigger or tap Read tag.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Could not connect to the hand scanner.");
    } finally {
      setBusy(false);
    }
  };

  const readTag = async (): Promise<void> => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    setBusy(true);
    setStatus("Reading… hold the reader to the tag.");
    try {
      await scanner.triggerAntennaScan(24);
      setStatus("Done. Read another, or scan a barcode with the trigger.");
    } catch {
      setStatus("Read failed — is the hand scanner still connected?");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = (): void => {
    scannerRef.current?.disconnectBle();
    scannerRef.current = null;
    setConnected(false);
    setDeviceName(null);
    setBattery(null);
    setStatus(null);
  };

  const useManual = (): void => {
    const epc = manual.trim().toUpperCase();
    if (!/^[A-Za-z0-9]{1,30}$/.test(epc)) {
      setStatus("Enter a valid EPC (letters/digits, up to 30 chars).");
      return;
    }
    onEpc(epc);
    setManual("");
    setStatus(null);
  };

  return (
    <div className="bg-white border border-secondary-200 rounded-md p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Camera path — always available. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setCameraOpen(true)}
        >
          <CameraIcon className="h-4 w-4 mr-1.5" />
          Scan with camera
        </Button>

        {/* R6 path — only where Web Bluetooth exists. */}
        {bleAvailable &&
          (connected ? (
            <>
              <Button type="button" size="sm" disabled={busy} onClick={() => void readTag()}>
                <SignalIcon className="h-4 w-4 mr-1.5" />
                Read tag (hand scanner)
              </Button>
              <button
                type="button"
                onClick={disconnect}
                className="text-xs text-secondary-500 hover:underline"
              >
                disconnect
              </button>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void connect()}
            >
              <SignalIcon className="h-4 w-4 mr-1.5" />
              Connect hand scanner
            </Button>
          ))}
      </div>

      {/* Manual / paste entry. */}
      <div className="flex items-center gap-2">
        <input
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") useManual();
          }}
          placeholder="…or type / paste an EPC"
          className="flex-1 min-w-0 border border-secondary-300 rounded px-2 py-1.5 text-sm font-mono"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
        />
        <Button type="button" variant="outline" size="sm" onClick={useManual}>
          Use
        </Button>
      </div>

      {/* Connection / status line. */}
      {(connected || status) && (
        <div className="text-xs text-secondary-500">
          {connected && (
            <span className="text-success-700 font-medium">
              ● {deviceName ?? "hand scanner"}
              {battery != null && ` · ${battery}%`}
            </span>
          )}
          {connected && status && " — "}
          {status}
        </div>
      )}

      <CameraEpcModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onEpc={(epc) => onEpc(epc.toUpperCase())}
      />
    </div>
  );
};

export default EpcScanner;
